/* eslint-disable no-nested-ternary */
import {
  Fragment,
  useState,
  useRef,
  useCallback,
  useEffect,
  useId,
} from 'react';
import { createPortal } from 'react-dom';
import { FloxSpinner } from '@tailwindComponents/FloxSpinner';

interface AutocompleteOption {
  id: string | number;
  label: string;
  groupLabel?: string;
  secondaryLabel?: string;
  [key: string]: any;
}

interface AutocompleteProps {
  label?: string;
  options: AutocompleteOption[];
  value: string;
  onChange: (value: AutocompleteOption | null) => void;
  onInputChange: (value: string) => void;
  isInvalid?: boolean;
  validationMessage?: string;
  helperText?: string;
  loading?: boolean;
  placeholder?: string;
  disabled?: boolean;
  noOptionsText?: string;
  actionLabel?: string;
  onActionClick?: () => void;
}

export function Autocomplete({
  label,
  options = [],
  value,
  onChange,
  onInputChange,
  isInvalid = false,
  validationMessage,
  helperText = '',
  loading = false,
  placeholder = '',
  disabled = false,
  noOptionsText = 'No options found',
  actionLabel,
  onActionClick,
}: AutocompleteProps) {
  const [showOptions, setShowOptions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const baseId = useId();
  const inputId = `${baseId}-input`;
  const listboxId = `${baseId}-listbox`;
  const errorId = `${baseId}-error`;

  // Update dropdown position when showing options
  useEffect(() => {
    if (showOptions && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [showOptions, value]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!showOptions) {
        if (e.key === 'ArrowDown' || e.key === 'Enter') {
          setShowOptions(true);
        }
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev < options.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev > 0 ? prev - 1 : options.length - 1
          );
          break;
        case 'Enter':
          e.preventDefault();
          if (highlightedIndex >= 0 && options[highlightedIndex]) {
            onChange(options[highlightedIndex]);
            setShowOptions(false);
            setHighlightedIndex(-1);
          }
          break;
        case 'Escape':
          setShowOptions(false);
          setHighlightedIndex(-1);
          inputRef.current?.blur();
          break;
        default:
          break;
      }
    },
    [showOptions, highlightedIndex, options, onChange]
  );

  const handleOptionClick = (option: AutocompleteOption) => {
    onChange(option);
    setShowOptions(false);
    setHighlightedIndex(-1);
    inputRef.current?.blur();
  };

  const showEmptyRow =
    !loading && options.length === 0 && !actionLabel && noOptionsText !== '';
  const showDropdown =
    showOptions &&
    (loading ||
      options.length > 0 ||
      !!actionLabel ||
      (showEmptyRow && value.length > 0));
  const hasGroupedOptions = options.some((option) => option.groupLabel);

  const dropdownContent = showDropdown ? (
    <ul
      ref={listRef}
      id={listboxId}
      role='listbox'
      style={{
        position: 'absolute',
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        width: dropdownPosition.width,
      }}
      className={`z-[9999] bg-white border border-gray-200 rounded-lg shadow-lg overflow-auto ${
        hasGroupedOptions ? 'max-h-[320px]' : 'max-h-[200px]'
      }`}
    >
      {loading && options.length === 0 ? (
        <li className='px-3 py-2 text-sm text-gray-500 flex items-center gap-2'>
          <FloxSpinner size='sm' />
          Searching…
        </li>
      ) : (
        <>
          {showEmptyRow && (
            <li className='px-3 py-2 text-sm text-gray-500'>{noOptionsText}</li>
          )}
          {options.map((option, idx) => (
            <Fragment key={option.id}>
              {option.groupLabel &&
                option.groupLabel !== options[idx - 1]?.groupLabel && (
                  <li
                    role='presentation'
                    aria-hidden='true'
                    className={`sticky top-0 z-10 bg-white px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500 select-none ${
                      idx > 0 ? 'border-t border-gray-200 mt-1' : ''
                    }`}
                  >
                    {option.groupLabel}
                  </li>
                )}
              <li
                id={`${listboxId}-option-${idx}`}
                role='option'
                aria-selected={highlightedIndex === idx}
              >
                <button
                  type='button'
                  title={
                    option.secondaryLabel
                      ? `${option.label} ${option.secondaryLabel}`
                      : option.label
                  }
                  aria-label={
                    option.groupLabel
                      ? `${option.label}${
                          option.secondaryLabel
                            ? ` ${option.secondaryLabel}`
                            : ''
                        } (${option.groupLabel})`
                      : undefined
                  }
                  className={`w-full truncate text-left px-3 py-2 text-sm cursor-pointer transition-colors ${
                    highlightedIndex === idx
                      ? 'bg-blue-50 text-primary'
                      : 'hover:bg-gray-50'
                  }`}
                  onMouseDown={() => handleOptionClick(option)}
                  onMouseEnter={() => setHighlightedIndex(idx)}
                  tabIndex={-1}
                >
                  {option.secondaryLabel ? (
                    <>
                      <span className='font-semibold'>{option.label}</span>
                      <span className='ml-2 font-normal text-gray-500'>
                        {option.secondaryLabel}
                      </span>
                    </>
                  ) : (
                    option.label
                  )}
                </button>
              </li>
            </Fragment>
          ))}
          {actionLabel && onActionClick && (
            <>
              {options.length > 0 && (
                <li className='border-t border-gray-100' role='separator' />
              )}
              <li>
                <button
                  type='button'
                  className='w-full text-left px-3 py-2 text-sm cursor-pointer transition-colors text-primary font-medium hover:bg-blue-50'
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onActionClick();
                    setShowOptions(false);
                  }}
                  tabIndex={-1}
                >
                  {actionLabel}
                </button>
              </li>
            </>
          )}
        </>
      )}
    </ul>
  ) : null;

  return (
    <div ref={containerRef} className='flex flex-col w-full'>
      {label && (
        <label
          htmlFor={inputId}
          className='text-sm font-medium text-gray-900'
        >
          {label}
        </label>
      )}
      <div className='relative'>
        <input
          id={inputId}
          ref={inputRef}
          type='text'
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete='off'
          autoCorrect='off'
          autoCapitalize='off'
          spellCheck='false'
          onChange={(e) => {
            const newValue = e.target.value;
            onInputChange(newValue);
            setShowOptions(true);
            setHighlightedIndex(-1);
            if (newValue === '') {
              onChange(null);
            }
          }}
          onFocus={() => setShowOptions(true)}
          onBlur={() => setTimeout(() => setShowOptions(false), 150)}
          onKeyDown={handleKeyDown}
          className={`w-full rounded-md border mt-2 h-8 ${
            isInvalid
              ? 'border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
              : 'border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20'
          } ${
            disabled ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''
          } py-1.5 px-3 pr-8 text-sm focus:outline-none transition-colors`}
          aria-invalid={isInvalid}
          aria-describedby={isInvalid ? errorId : undefined}
          aria-expanded={showDropdown}
          aria-autocomplete='list'
          aria-controls={listboxId}
          aria-activedescendant={
            highlightedIndex >= 0
              ? `${listboxId}-option-${highlightedIndex}`
              : undefined
          }
          role='combobox'
        />
        {loading && (
          <div className='absolute right-2 top-1/2 -translate-y-1/2 mt-1'>
            <FloxSpinner size='sm' />
          </div>
        )}
      </div>

      {/* Portal the dropdown to body so it's not clipped by overflow:hidden containers */}
      {typeof document !== 'undefined' &&
        createPortal(dropdownContent, document.body)}

      {isInvalid && validationMessage && (
        <p id={errorId} className='mt-2 text-sm text-red-600'>
          {validationMessage}
        </p>
      )}
      {helperText && !isInvalid && (
        <p className='mt-2 text-sm text-gray-500'>{helperText}</p>
      )}
    </div>
  );
}
